import React, { useCallback, useId } from 'react';
import clx from 'classnames';
import { ChevronDown } from 'lucide-react';
import { snapToStep, atMinutes } from '../../TimeStepper';
import {
  parseHHmmToTotal,
  useTimePickerBounds,
} from '../timePickerShared';
import styles from '../CustomTimePicker.module.scss';

/**
 * Mobile: picker orario nativo del sistema (iOS/Android).
 * Su iOS `step` può non limitare i minuti nella rotella: lo snap in onChange/onBlur è obbligatorio.
 */
const MobileTimePicker = ({
  value,
  setValue,
  placeholder,
  title,
  minTime,
  maxTime,
  error,
  disabled = false,
  timeStep = 5,
}) => {
  const { step, safeValue, boundOpts, display, minInput, maxInput } = useTimePickerBounds(
    value,
    minTime,
    maxTime,
    timeStep,
  );

  const commitNativeValue = useCallback(
    (raw) => {
      if (disabled || !raw) return;
      const total = parseHHmmToTotal(raw);
      if (total === null) return;
      const snapped = snapToStep(total, step);
      const clamped = Math.max(boundOpts.min, Math.min(boundOpts.max, snapped));
      setValue?.(atMinutes(safeValue, clamped, boundOpts));
    },
    [disabled, step, boundOpts, setValue, safeValue],
  );

  const handleNativeChange = useCallback(
    (event) => {
      commitNativeValue(event.target.value);
    },
    [commitNativeValue],
  );

  // iOS a volte conferma l'orario solo su blur/input; onChange da solo non basta.
  const handleNativeInput = useCallback(
    (event) => {
      commitNativeValue(event.target.value);
    },
    [commitNativeValue],
  );

  const inputLabel = title || placeholder || 'Orario';
  const labelId = useId();

  return (
    <div
      className={clx(styles.picker_root, {
        [styles.picker_error]: !!error,
        [styles.picker_disabled]: disabled,
        [styles.picker_native]: true,
      })}
    >
      {(title || placeholder) && (
        <span className={styles.label} id={labelId}>
          {title || placeholder}
        </span>
      )}

      <div className={styles.trigger}>
        <span className={styles.trigger_value} aria-hidden>
          {display}
        </span>
        <ChevronDown size={18} strokeWidth={2.3} className={styles.trigger_chevron} aria-hidden />
        <input
          type="time"
          className={styles.native_input}
          value={display}
          step={step * 60}
          min={minInput}
          max={maxInput}
          disabled={disabled}
          aria-label={inputLabel}
          aria-labelledby={title || placeholder ? labelId : undefined}
          onChange={handleNativeChange}
          onInput={handleNativeInput}
          onBlur={handleNativeChange}
        />
      </div>

      {error ? <span className={styles.error_text}>{error}</span> : null}
    </div>
  );
};

export default MobileTimePicker;
